import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-10">
      <h2 className="font-serif text-2xl text-sage-800">{title}</h2>
      <div className="mt-4 space-y-4 text-sage-700/90">{children}</div>
    </div>
  );
}

export default function Accessibility() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Accessibility Statement"
        intro="We are committed to ensuring digital accessibility for all users, including those with disabilities."
      />

      <section className="bg-cream-50">
        <div className="section max-w-3xl">
          <p className="text-sage-700/90">
            We are continually improving the user experience and applying the relevant accessibility standards to our
            website.
          </p>

          <Section title="Recommended Accessibility Tools &amp; Settings">
            <p>
              To enhance your browsing experience, we recommend utilizing the accessibility features built into your
              devices and software:
            </p>
            <ul className="ml-4 space-y-3">
              {[
                {
                  label: "Operating Systems",
                  detail:
                    "Ensure you are using the most current version of your operating system (iOS, Android, Windows, or macOS) to benefit from the latest accessibility enhancements.",
                },
                {
                  label: "Navigation & Visibility",
                  detail:
                    "Utilize system-level tools for keyboard navigation, screen magnification, text-size increases, and high-contrast color modes.",
                },
                {
                  label: "Screen Readers",
                  detail:
                    'Most modern browsers and operating systems provide "Speak Screen" or "Narrator" functions to read content aloud.',
                },
              ].map(({ label, detail }) => (
                <li key={label} className="flex gap-2">
                  <span aria-hidden="true" className="mt-0.5 shrink-0 text-blush-400">•</span>
                  <span>
                    <strong>{label}:</strong> {detail}
                  </span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Multimedia Content">
            <div className="rounded-2xl border border-sage-100 bg-cream-100/60 p-6 space-y-4">
              <div>
                <h3 className="font-serif text-lg text-sage-800">Closed Captioning</h3>
                <p className="mt-1">
                  We strive to provide synchronized captions for our video content. For videos where captions may not
                  be available, written descriptions or alternative formats for our services are available upon request.
                </p>
              </div>
              <div>
                <h3 className="font-serif text-lg text-sage-800">Volume Controls</h3>
                <p className="mt-1">
                  Please ensure both your device volume and the individual media player controls are adjusted for an
                  optimal experience.
                </p>
              </div>
            </div>
          </Section>

          <Section title="Ongoing Efforts and Standard">
            <p>
              We aim to adhere to the Web Content Accessibility Guidelines (<strong>WCAG 2.1, Level AA</strong>) where
              feasible. Please be aware that our efforts are ongoing as we work to implement relevant improvements over
              time.
            </p>
          </Section>

          <Section title="Accessibility Inquiries &amp; Secure Communication">
            <div className="rounded-2xl border border-blush-100 bg-blush-50/60 p-6">
              <p className="font-medium text-sage-800">Important Privacy Notice</p>
              <p className="mt-1">
                Please be advised that standard email is not a secure form of communication. Avoid sending sensitive
                medical, legal, or personal confidential information via the form below or via unencrypted email.
              </p>
            </div>
            <ul className="ml-4 space-y-2">
              <li className="flex gap-2">
                <span aria-hidden="true" className="text-blush-400">•</span>
                <span>
                  <strong>Minors:</strong> This website and its contact forms are not intended for use by individuals
                  under the age of 18.
                </span>
              </li>
              <li className="flex gap-2">
                <span aria-hidden="true" className="text-blush-400">•</span>
                <span>
                  <strong>Response Time:</strong> We aim to respond to all inquiries within one business day. If you
                  have not heard from us within this timeframe, please contact our office by phone, as your message may
                  not have been received due to technical issues.
                </span>
              </li>
            </ul>
          </Section>

          <Section title="Assistance and Feedback">
            <p>
              If you encounter any difficulty accessing specific content or features on this website, or if you have
              suggestions on how we can improve accessibility, please{" "}
              <Link to="/#contact" className="text-blush-500 underline hover:text-blush-600">
                contact us directly
              </Link>
              . We will work with you to provide the information or service you need through a communication method
              that is accessible to you.
            </p>
          </Section>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/#contact" className="btn-primary">
              Contact us
            </Link>
            <Link to="/" className="btn-secondary">
              Back to home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
